/**
 * ViewWorkerPage.js — Worker profile with new design system
 *
 * Route: /worker/:id (ProtectedRoute)
 *
 * Layout (matches profile.html from Claude Design handoff):
 *   Left column (sticky on desktop):
 *     IdentityCard  — avatar (with status pill overlay), name, shift+rate pills, joined/exit dates
 *     BalanceCard   — dark gradient card with current balance, earned, paid
 *     QuickActionBar — Call / Note / Pay / Edit / More (5 icon buttons)
 *   Right column (scrollable):
 *     Tabs: Attendance · Notes · Transactions · Payslips
 *
 * Attendance tab:
 *   - Monthly stats (present / absent / attendance %)
 *   - Calendar view  — navigable month grid, colour-coded by status
 *   - List view      — week-grouped ledger rows
 *   - Tap any row/day → DayDrawer (bottom-sheet with full shift detail)
 *
 * Notes tab:
 *   - Add-note form + list (functional, wired to Directus /items/notes)
 *
 * Transactions tab:
 *   - Combined earnings + payouts in bank-style ledger, grouped by date
 *   - Running balance derived from current_balance working backwards
 *
 * Payslips tab:
 *   - List of payslips (cycle label, payout date, net due, status)
 *   - Row click opens PayslipDetailModal (drawer-style, with approve/pay actions)
 *
 * Data loading: all five API calls fire in parallel on mount via Promise.all.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, showAlert } from '../components/UI';
import DayDrawer from '../components/DayDrawer';
import PaymentModal from '../components/PaymentModal';
import {
  getWorker,
  getWorkerAttendance,
  getWorkerEarnings,
  getWorkerNotes,
  getWorkerPayouts,
  getWorkerPayslips,
  getWorkerLeaveRequests,
  createLeaveRequest,
  updateLeaveRequest,
  getLeavePolicy,
  getWorkerDeductions,
} from '../api/directus';
import { formatCurrency, formatDate, formatDateTime, formatDuration, getISTToday } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

// ── Attendance status helpers ─────────────────────────────────────────────────

/** Map Directus statuses to visual calendar categories. */
function attCategory(status) {
  if (['present', 'break', 'checkout', 'approved'].includes(status)) return 'present';
  if (status === 'absent') return 'absent';
  if (status === 'leave') return 'leave';
  if (status === 'weekly_off') return 'off';
  return null;
}

/**
 * Select the appropriate Material Symbol icon for a status.
 * is_approved takes precedence — show the verified icon on any approved record.
 */
function attIcon(status, is_approved) {
  if (is_approved || status === 'approved') return 'verified';
  if (['present', 'break', 'checkout'].includes(status)) return 'check_circle';
  if (status === 'absent') return 'cancel';
  if (status === 'leave') return 'beach_access';
  if (status === 'weekly_off') return 'event_busy';
  return 'circle';
}

/** Format minutes as "Xh YYm", returning '—' for zero or null. */
function fmtHM(min) {
  if (!min) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

// ── IdentityCard ──────────────────────────────────────────────────────────────

function IdentityCard({ worker }) {
  const shiftName = worker.shift?.name || '';
  const rateName  = worker.rate?.name  || '';
  const joined    = formatDate(worker.doj, { day: 'numeric', month: 'short', year: 'numeric' });
  // doe (date of exit) is null for active workers
  const left      = worker.doe ? formatDate(worker.doe, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  return (
    <section className="pf-identity fade-up">
      {/* Avatar wrapper lets the status pill overlap from the bottom */}
      <div className="pf-avatar-wrap">
        <Avatar worker={worker} size={88} />
        <span className={`pf-status-pill ${worker.status || 'active'}`}>
          {worker.status === 'inactive' ? 'Inactive' : 'Active'}
        </span>
      </div>
      <div className="pf-identity-meta">
        <h2 className="pf-identity-name">{worker.name}</h2>
        {(shiftName || rateName) && (
          <div className="pf-meta-pills">
            {shiftName && <span className="pf-meta-pill pf-meta-pill--shift">{shiftName}</span>}
            {rateName  && <span className="pf-meta-pill pf-meta-pill--rate">₹ {rateName}</span>}
          </div>
        )}
        <div className="pf-identity-dates">
          <span className="pf-date-item">
            <span className="material-symbols-rounded">calendar_today</span>
            Joined {joined}
          </span>
          {left && (
            <span className="pf-date-item pf-date-item--exit">
              <span className="material-symbols-rounded">event_busy</span>
              Left {left}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ── BalanceCard ───────────────────────────────────────────────────────────────

function BalanceCard({ worker }) {
  const fmt = n => n != null
    ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : '—';

  return (
    <section className="pf-balance-card fade-up" style={{ animationDelay: '0.04s' }}>
      <div className="pf-balance-eyebrow">Current Balance</div>
      <div className="pf-balance-amount num">
        <span className="pf-rupee">₹</span>{fmt(worker.current_balance ?? 0)}
      </div>
      <div className="pf-balance-meta">
        <div>
          Total earned
          <strong className="num">₹{fmt(worker.total_earnings)}</strong>
        </div>
        <div>
          Total paid out
          <strong className="num">₹{fmt(worker.total_payouts)}</strong>
        </div>
      </div>
    </section>
  );
}

// ── QuickActionBar ────────────────────────────────────────────────────────────

function QuickActionBar({ worker, onNote, onPay, isManager }) {
  return (
    <div className="pf-qab fade-up" style={{ animationDelay: '0.06s' }}>
      {worker.phone && (
        <a href={`tel:${worker.phone}`} className="pf-qab-item">
          <span className="pf-qab-ico"><span className="material-symbols-rounded">call</span></span>
          <span>Call</span>
        </a>
      )}
      <button className="pf-qab-item" onClick={onNote}>
        <span className="pf-qab-ico"><span className="material-symbols-rounded">edit_note</span></span>
        <span>Note</span>
      </button>
      {isManager && (
        <button className="pf-qab-item primary" onClick={onPay}>
          <span className="pf-qab-ico"><span className="material-symbols-rounded">currency_rupee</span></span>
          <span>Pay</span>
        </button>
      )}
      {/* Edit and More are placeholders — functionality to be wired in a later phase */}
      <button className="pf-qab-item" onClick={() => showAlert('Edit profile — coming soon', 'info')}>
        <span className="pf-qab-ico"><span className="material-symbols-rounded">edit</span></span>
        <span>Edit</span>
      </button>
      <button className="pf-qab-item" onClick={() => showAlert('More actions — coming soon', 'info')}>
        <span className="pf-qab-ico"><span className="material-symbols-rounded">more_horiz</span></span>
        <span>More</span>
      </button>
    </div>
  );
}

// ── ProfileTabBar ─────────────────────────────────────────────────────────────

/**
 * Tab bar scoped to the profile page (pf-tab prefix) to avoid conflicting
 * with the existing .tab-btn / .tabs styles used on other pages.
 */
function ProfileTabBar({ tabs, current, onChange }) {
  return (
    <div role="tablist" className="pf-tabs">
      {tabs.map(t => (
        <button
          key={t.key}
          role="tab"
          aria-selected={current === t.key}
          className="pf-tab"
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.count != null && <span className="pf-tab-count num">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ── CalendarView ──────────────────────────────────────────────────────────────

function CalendarView({ records, onPick }) {
  const today = new Date();
  const [month, setMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );

  // Index records for this month by calendar day
  const recByDay = useMemo(() => {
    const map = {};
    records.forEach(r => {
      // Directus date strings are YYYY-MM-DD; parse as local date to avoid UTC shift
      const [y, mo, d] = r.date.split('-').map(Number);
      const date = new Date(y, mo - 1, d);
      if (date.getFullYear() === month.getFullYear() && date.getMonth() === month.getMonth()) {
        map[date.getDate()] = r;
      }
    });
    return map;
  }, [records, month]);

  const startDow = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dows = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="pf-cal">
      <div className="pf-cal-head">
        <div className="pf-cal-nav">
          <button
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            aria-label="Previous month"
          >
            <span className="material-symbols-rounded">chevron_left</span>
          </button>
        </div>
        <h3>{month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h3>
        <div className="pf-cal-nav">
          <button
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            aria-label="Next month"
          >
            <span className="material-symbols-rounded">chevron_right</span>
          </button>
        </div>
      </div>

      <div className="pf-cal-grid">
        {dows.map(d => <div key={d} className="pf-cal-dow">{d}</div>)}
        {cells.map((day, i) => {
          if (day == null) return <div key={`e${i}`} className="pf-cal-cell empty" />;
          const rec = recByDay[day];
          const cat = rec ? attCategory(rec.status) : null;
          const isToday =
            day === today.getDate() &&
            month.getMonth() === today.getMonth() &&
            month.getFullYear() === today.getFullYear();
          const isFuture = new Date(month.getFullYear(), month.getMonth(), day) > today;
          // Pending approval: any approvable status that hasn't been signed off yet
          const isPending = rec && !rec.is_approved && ['checkout', 'absent', 'leave', 'weekly_off'].includes(rec.status);
          const cls = [
            'pf-cal-cell',
            cat ? `s-${cat}` : '',
            isPending ? 's-pending' : '',          // dashed amber ring overlay
            rec && !cat && !isFuture ? 's-unmarked' : '', // record exists but status=unmarked → grey
            !cat && isFuture  ? 's-future'  : '',  // future with no record → faded
            isToday ? 'today' : '',
          ].filter(Boolean).join(' ');

          return (
            <button key={day} className={cls} onClick={() => rec && onPick(rec)}>
              {day}
            </button>
          );
        })}
      </div>

      <div className="pf-cal-legend">
        <span><i style={{ background: 'color-mix(in srgb, #6f8a3a 40%, #fff)' }} />Present</span>
        <span><i style={{ background: 'color-mix(in srgb, #b04025 40%, #fff)' }} />Absent</span>
        <span><i style={{ background: 'color-mix(in srgb, #3a6e8e 40%, #fff)' }} />On Leave</span>
        <span><i style={{ background: 'color-mix(in srgb, #6b5fa5 40%, #fff)' }} />Weekly Off</span>
        <span><i style={{ background: 'color-mix(in srgb, #8a7a66 18%, #fff)' }} />Unmarked</span>
        <span><i style={{ background: 'transparent', outline: '2px dashed #c79221', outlineOffset: '-1px' }} />Pending approval</span>
      </div>
    </div>
  );
}

// ── AttendanceList ────────────────────────────────────────────────────────────

/**
 * Week-grouped ledger-style list of attendance records.
 * Visual rhythm mirrors the Transactions tab for consistency.
 */
function AttendanceList({ records, onPick }) {
  const groups = useMemo(() => {
    const gs = [];
    let cur = null;
    records.slice(0, 90).forEach(r => {
      // Parse date locally so it matches calendar
      const [y, mo, d] = r.date.split('-').map(Number);
      const date = new Date(y, mo - 1, d);
      const dow = (date.getDay() + 6) % 7; // Mon=0
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - dow);
      const key = weekStart.toISOString().slice(0, 10);

      if (!cur || cur.key !== key) {
        cur = { key, weekStart, items: [], present: 0, totalNet: 0 };
        gs.push(cur);
      }
      cur.items.push({ ...r, _date: date });
      if (attCategory(r.status) === 'present') cur.present += 1;
      cur.totalNet += Math.max(0, (r.shift_duration || 0) - (r.break_duration || 0));
    });
    return gs;
  }, [records]);

  function rowMeta(r) {
    const cat = attCategory(r.status);
    if (cat === 'present') return { icon: attIcon(r.status, r.is_approved), cls: 't-present' };
    if (cat === 'absent')  return { icon: 'cancel',        cls: 't-absent'  };
    if (cat === 'leave')   return { icon: 'beach_access',  cls: 't-leave'   };
    if (cat === 'off')     return { icon: 'event_busy',    cls: 't-off'     };
    return { icon: 'circle', cls: '' };
  }

  return (
    <div>
      {groups.map(g => {
        const end = new Date(g.weekStart);
        end.setDate(end.getDate() + 6);
        const weekLabel =
          `${g.weekStart.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` +
          ` – ${end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;

        return (
          <div key={g.key} className="pf-ledger-day">
            <div className="pf-ledger-day-head">
              <span>{weekLabel}</span>
              <span className="pf-running-bal num">
                {g.present}p · {fmtHM(g.totalNet)}
              </span>
            </div>
            <div className="pf-ledger-list">
              {g.items.map((r, i) => {
                const { icon, cls } = rowMeta(r);
                const dayName = r._date.toLocaleDateString('en-IN', { weekday: 'short' });
                const dateStr = r._date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                const net = Math.max(0, (r.shift_duration || 0) - (r.break_duration || 0));
                const punchIn  = r.punch_in?.timestamp  ? formatDateTime(r.punch_in.timestamp)  : null;
                const punchOut = r.punch_out?.timestamp ? formatDateTime(r.punch_out.timestamp) : null;

                return (
                  <button
                    key={i}
                    className={`pf-ledger-row pf-att-row ${cls}`}
                    onClick={() => onPick(r)}
                  >
                    <div className="pf-ledger-ico">
                      <span className="material-symbols-rounded">{icon}</span>
                    </div>
                    <div className="pf-ledger-meta">
                      <p className="pf-ledger-kind">{dayName}, {dateStr}</p>
                      <p className="pf-ledger-ref">
                        {punchIn ? (
                          <>
                            <span className="pf-att-time">
                              <span className="material-symbols-rounded">login</span>{punchIn}
                            </span>
                            <span className="pf-att-sep">→</span>
                            <span className="pf-att-time">
                              <span className="material-symbols-rounded">logout</span>
                              {punchOut || <span className="pf-running-mini">Running</span>}
                            </span>
                            {r.break_duration > 0 && (
                              <>
                                <span className="pf-att-sep">·</span>
                                <span className="pf-att-time">
                                  <span className="material-symbols-rounded">pause_circle</span>
                                  {fmtHM(r.break_duration)}
                                </span>
                              </>
                            )}
                          </>
                        ) : (
                          <span style={{ color: 'var(--fg-muted)' }}>
                            {r.status === 'absent'     ? 'Absent'     :
                             r.status === 'leave'      ? 'On Leave'   :
                             r.status === 'weekly_off' ? 'Weekly Off' : 'Not started'}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="pf-ledger-amounts">
                      {net > 0 ? (
                        <>
                          <div className="pf-amt num pf-att-net">{fmtHM(net)}</div>
                          <div className="pf-bal num">net worked</div>
                        </>
                      ) : (
                        <div className={`pf-amt num ${r.status === 'absent' ? 'pf-att-absent' : ''}`}>
                          {r.status === 'absent'     ? 'Absent'     :
                           r.status === 'leave'      ? 'On Leave'   :
                           r.status === 'weekly_off' ? 'Weekly Off' : r.status}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── NotesTab ──────────────────────────────────────────────────────────────────

// Notes can only be added via the DayDrawer (attendance record required).
// This tab is read-only; tap "View record" on any note to open its shift in DayDrawer.
function NotesTab({ notes, onOpenDrawer }) {
  /** Heuristic kind classifier used for icon tinting — not stored in Directus. */
  function noteKind(n) {
    const txt = (n.content || '').toLowerCase();
    if (txt.includes('great') || txt.includes('good') || txt.includes('excellent') || txt.includes('early')) return 'praise';
    if (txt.includes('warn') || txt.includes('late') || txt.includes('absent') || txt.includes('missing')) return 'warning';
    return 'note';
  }
  const kindIcon = { praise: 'star', warning: 'warning', note: 'edit_note', system: 'info' };

  // Skip legacy notes that were created without an attendance link — they
  // can't be sorted by shift date and have no "View record" target.
  const linkedNotes = notes.filter(n => n.attendance);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {linkedNotes.map((n, i) => {
        const k = noteKind(n);
        return (
          <article key={n.id || i} className={`pf-note-card k-${k}`}>
            <div className="pf-note-icon">
              <span className="material-symbols-rounded">{kindIcon[k]}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pf-note-meta">
                <strong>{n.user_created?.first_name || 'Supervisor'}</strong>
                <span className="pf-dot" />
                <span>{formatDate(n.attendance.date)}</span>
                <button
                  className="pf-note-view-btn"
                  title="View attendance record"
                  onClick={() => onOpenDrawer(n.attendance)}
                >
                  <span className="material-symbols-rounded">open_in_new</span> View record
                </button>
              </div>
              <p className="pf-note-text">{n.content}</p>
            </div>
          </article>
        );
      })}

      {linkedNotes.length === 0 && (
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, padding: '8px 2px' }}>No notes yet.</p>
      )}
    </div>
  );
}

// ── TransactionsTab ───────────────────────────────────────────────────────────

const MODE_LABEL      = { upi: 'UPI', bank: 'Bank Transfer', cash: 'Cash' };
const TYPE_LABEL      = { wages: 'Wages', overtime: 'Overtime', bonus: 'Bonus', allowance: 'Allowance' };
const DEDUCTION_LABEL = { leave: 'Leave Deduction', tax: 'Tax', damages: 'Damages', adhoc: 'Deduction' };
const DEDUCTION_ICO   = { leave: 'beach_access', tax: 'receipt_long', damages: 'warning', adhoc: 'remove_circle' };

/** Strip trailing decimal zeros from a stored rate (66.0000 → 66, 53.25 → 53.25). */
const fmtRate = r => parseFloat(Number(r).toFixed(2));

/** Extract the primary hours and rate for display, keyed by earnings type. */
function earningChips(e) {
  const isOT = e.type === 'overtime';
  const hours   = isOT ? (parseFloat(e.OT_hours)  || 0) : (parseFloat(e.wage_hours) || 0);
  const rate    = isOT ? (parseFloat(e.OT_rate)   || 0) : (parseFloat(e.wage_rate)  || 0);
  // Legacy combined record: wages row that also carries OT hours
  const legacyOTHours = (!isOT && parseFloat(e.OT_hours)  > 0) ? parseFloat(e.OT_hours)  : 0;
  const legacyOTRate  = (!isOT && parseFloat(e.OT_rate)   > 0) ? parseFloat(e.OT_rate)   : 0;
  return { hours, rate, legacyOTHours, legacyOTRate, remarks: e.remarks || '' };
}

/**
 * Combines earnings (positive), payouts (negative), and deductions (negative)
 * into a single bank-style ledger grouped by month. Running balance is derived
 * backwards from the computed current balance — always on the full unfiltered
 * data so individual row balances stay accurate with a date filter active.
 */
function TransactionsTab({ earnings, payouts, deductions, worker, attendance, onOpenDrawer }) {
  const [fromDate, setFromDate] = useState('');
  const [toDate,   setToDate]   = useState('');

  // Derive from loaded records (DB field can lag behind the balance-recalc flow)
  const currentBalance =
    earnings.reduce(  (s, e) => s + Number(e.amount || 0), 0) -
    payouts.reduce(   (s, p) => s + Number(p.amount || 0), 0) -
    (deductions || []).reduce((s, d) => s + Number(d.amount || 0), 0);

  const fmt = n => Math.abs(Number(n)).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  // Full ledger — always unfiltered so balance stamps are accurate
  const allLedger = useMemo(() => {
    const items = [
      ...earnings.map(e => ({
        date:             e.date,
        dateCreated:      e.date_created,
        type:             'earning',
        kind:             TYPE_LABEL[e.type] || e.type || 'Wages',
        earningType:      e.type || 'wages',
        deductionType:    null,
        rateName:         e.rate?.name || null,
        chips:            earningChips(e),
        amount:           Number(e.amount),
        // Prefer the attendance array record (full depth) over e.attendance
        attendanceRecord: (attendance || []).find(a => a.id === (e.attendance?.id ?? e.attendance)) || e.attendance || null,
      })),
      ...payouts.map(p => ({
        date:             p.date,
        dateCreated:      p.date_created,
        type:             'payout',
        kind:             MODE_LABEL[p.mode] || p.mode || 'Payment',
        earningType:      null,
        deductionType:    null,
        rateName:         null,
        chips:            { hours: 0, rate: 0, legacyOTHours: 0, legacyOTRate: 0, remarks: p.remarks || '' },
        amount:           -Number(p.amount),
        attendanceRecord: (attendance || []).find(a => a.date === p.date) || null,
      })),
      ...(deductions || []).map(d => ({
        date:             d.date,
        dateCreated:      d.date_created,
        type:             'deduction',
        kind:             DEDUCTION_LABEL[d.type] || 'Deduction',
        earningType:      null,
        deductionType:    d.type || 'adhoc',
        rateName:         null,
        chips:            { hours: 0, rate: 0, legacyOTHours: 0, legacyOTRate: 0, remarks: d.remarks || '' },
        amount:           -Number(d.amount), // negative — reduces balance
        // Deductions may be linked to an attendance record via d.attendance
        attendanceRecord: (attendance || []).find(a => a.id === (d.attendance?.id ?? d.attendance)) || null,
      })),
    ];

    // Sort newest first; dateCreated breaks ties within the same day
    items.sort((a, b) => {
      const d = new Date(b.date) - new Date(a.date);
      return d !== 0 ? d : new Date(b.dateCreated || b.date) - new Date(a.dateCreated || a.date);
    });

    // Backwards balance: stamp each row with the running balance after that entry
    let bal = currentBalance;
    items.forEach(t => { t.balance = bal; bal -= t.amount; });

    return items;
  }, [earnings, payouts, deductions, currentBalance]);

  // Apply date filter for display only — balances come from allLedger
  const isFiltered = fromDate || toDate;
  const visibleLedger = useMemo(() => {
    if (!isFiltered) return allLedger;
    return allLedger.filter(t => {
      if (fromDate && t.date < fromDate) return false;
      if (toDate   && t.date > toDate)   return false;
      return true;
    });
  }, [allLedger, fromDate, toDate, isFiltered]);

  // Group by month
  const monthGroups = useMemo(() => {
    const gs = {};
    visibleLedger.forEach(t => {
      const key = t.date.slice(0, 7);
      if (!gs[key]) gs[key] = [];
      gs[key].push(t);
    });
    return Object.entries(gs).sort((a, b) => b[0].localeCompare(a[0]));
  }, [visibleLedger]);

  // All-time totals
  const totalEarned   = earnings.reduce(  (s, e) => s + Number(e.amount || 0), 0);
  const totalPaid     = payouts.reduce(   (s, p) => s + Number(p.amount || 0), 0);
  const totalDeducted = (deductions || []).reduce((s, d) => s + Number(d.amount || 0), 0);

  // Period totals (filtered)
  const filtEarned   = visibleLedger.filter(t => t.type === 'earning')  .reduce((s, t) => s + t.amount,            0);
  const filtPaid     = visibleLedger.filter(t => t.type === 'payout')   .reduce((s, t) => s + Math.abs(t.amount), 0);
  const filtDeducted = visibleLedger.filter(t => t.type === 'deduction').reduce((s, t) => s + Math.abs(t.amount), 0);

  const EARNING_ICO = { wages: 'work', overtime: 'more_time', bonus: 'emoji_events', allowance: 'savings' };
  const txIcon = t =>
    t.type === 'payout'    ? 'payments' :
    t.type === 'deduction' ? (DEDUCTION_ICO[t.deductionType] || 'remove_circle') :
    (EARNING_ICO[t.earningType] || 'work');

  /** Render the structured detail chips below the kind label. */
  function DetailChips({ t }) {
    const { hours, rate, legacyOTHours, legacyOTRate, remarks } = t.chips;
    const dateStr = formatDate(t.date, { day: 'numeric', month: 'short' });
    const chips = [];
    if (hours > 0) chips.push(
      <span key="h" className="pf-detail-chip">
        <span className="material-symbols-rounded">schedule</span>{hours}h
      </span>
    );
    if (rate > 0) chips.push(
      <span key="r" className="pf-detail-chip">₹{fmtRate(rate)}/h</span>
    );
    // Legacy combined record: show OT portion inline
    if (legacyOTHours > 0) chips.push(
      <span key="oth" className="pf-detail-chip">
        <span className="material-symbols-rounded">bolt</span>{legacyOTHours}h OT
      </span>
    );
    if (legacyOTRate > 0) chips.push(
      <span key="otr" className="pf-detail-chip">₹{fmtRate(legacyOTRate)}/h</span>
    );
    if (remarks) chips.push(
      <span key="rem" className="pf-detail-chip">{remarks}</span>
    );
    return (
      <div className="pf-ledger-detail">
        <span className="pf-detail-date">{dateStr}</span>
        {chips.map((chip, i) => (
          <React.Fragment key={i}>
            <span className="pf-detail-sep">·</span>
            {chip}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* ── Date filter ── */}
      <div className="pf-tx-filters">
        <span className="material-symbols-rounded pf-tx-filter-ico">filter_list</span>
        <input
          type="date" className="pf-tx-date-input" value={fromDate}
          onChange={e => setFromDate(e.target.value)}
        />
        <span className="pf-tx-filter-sep">–</span>
        <input
          type="date" className="pf-tx-date-input" value={toDate}
          onChange={e => setToDate(e.target.value)}
        />
        {isFiltered && (
          <button className="pf-tx-clear" onClick={() => { setFromDate(''); setToDate(''); }}>
            Clear
          </button>
        )}
      </div>

      {/* ── Summary ── */}
      <div className="pf-ledger-summary">
        <div className="pf-stat pf-earnings">
          <span className="pf-stat-num num">+₹{fmt(isFiltered ? filtEarned : totalEarned)}</span>
          <span className="pf-stat-label">{isFiltered ? 'Earned · period' : 'Total earned'}</span>
          {isFiltered && <span className="pf-stat-alltime num">+₹{fmt(totalEarned)} all time</span>}
        </div>
        {totalDeducted > 0 && (
          <div className="pf-stat pf-deductions">
            <span className="pf-stat-num num">−₹{fmt(isFiltered ? filtDeducted : totalDeducted)}</span>
            <span className="pf-stat-label">{isFiltered ? 'Deducted · period' : 'Total deducted'}</span>
            {isFiltered && <span className="pf-stat-alltime num">−₹{fmt(totalDeducted)} all time</span>}
          </div>
        )}
        <div className="pf-stat pf-payouts">
          <span className="pf-stat-num num">−₹{fmt(isFiltered ? filtPaid : totalPaid)}</span>
          <span className="pf-stat-label">{isFiltered ? 'Paid out · period' : 'Total paid out'}</span>
          {isFiltered && <span className="pf-stat-alltime num">−₹{fmt(totalPaid)} all time</span>}
        </div>
      </div>

      {/* ── Month-grouped ledger ── */}
      {monthGroups.map(([monthKey, txs]) => {
        const monthLabel = new Date(`${monthKey}-01T00:00:00`).toLocaleDateString('en-IN', {
          month: 'long', year: 'numeric',
        });
        return (
          <div key={monthKey} className="pf-ledger-month">
            <div className="pf-ledger-month-head">{monthLabel}</div>
            <div className="pf-ledger-list">
              {txs.map((t, i) => (
                <div
                  key={i}
                  className={`pf-ledger-row t-${t.type}${!t.attendanceRecord ? ' no-drill' : ''}`}
                  onClick={() => t.attendanceRecord && onOpenDrawer(t.attendanceRecord)}
                >
                  <div className="pf-ledger-ico">
                    <span className="material-symbols-rounded">{txIcon(t)}</span>
                  </div>
                  <div className="pf-ledger-meta">
                    <p className="pf-ledger-kind">
                      {t.kind}
                      {t.rateName && (
                        <span className="pf-rate-pill">{t.rateName}</span>
                      )}
                    </p>
                    <DetailChips t={t} />
                  </div>
                  <div className="pf-ledger-amounts">
                    <div className={`pf-amt num ${t.type === 'payout' ? 'pf-payout' : t.type === 'deduction' ? 'pf-deduction-amt' : 'pf-pos'}`}>
                      {t.amount > 0 ? `+₹${fmt(t.amount)}` : `−₹${fmt(Math.abs(t.amount))}`}
                    </div>
                    <div className={`pf-bal num${t.balance < 0 ? ' pf-bal-neg' : ''}`}>
                      {t.balance < 0 ? `Bal −₹${fmt(t.balance)}` : `Bal ₹${fmt(t.balance)}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {monthGroups.length === 0 && (
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, padding: '8px 2px' }}>
          {isFiltered ? 'No transactions in this date range.' : 'No transactions yet.'}
        </p>
      )}
    </div>
  );
}

// ── PayslipsTab ───────────────────────────────────────────────────────────────

/** Parse a YYYY-MM cycle label into a human-readable "March 2026" string. */
function cycleToLabel(cycleLabel) {
  if (!cycleLabel) return cycleLabel;
  const [y, m] = cycleLabel.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

const PAYSLIP_PILL = {
  draft:    'pill pill-pending',
  approved: 'pill pill-approved',
  paid:     'pill pill-paid',
};

const PAYSLIP_LABEL = {
  draft:    'Draft',
  approved: 'Approved',
  paid:     'Paid',
};

/**
 * Payslip list tab — shows all payslips for the worker, most recent first.
 * Data is fetched by ViewWorkerPage in a non-blocking background effect and
 * passed down so the tab reflects whatever state the fetch is in.
 * Row click opens PayslipDetailModal.
 */
function PayslipsTab({ worker, payslips, payslipsLoading }) {
  const navigate = useNavigate();

  if (payslipsLoading) {
    return <p style={{ color: 'var(--fg-muted)', fontSize: 13, padding: '8px 2px' }}>Loading payslips…</p>;
  }

  if (payslips.length === 0) {
    return (
      <div className="pf-payslip-placeholder">
        <div className="pf-payslip-icon">
          <span className="material-symbols-rounded">receipt_long</span>
        </div>
        <h3>No Payslips Yet</h3>
        <p>Payslips will appear here once generated for this worker.</p>
      </div>
    );
  }

  return (
    <table className="pf-payslip-table">
      <thead>
        <tr>
          <th>Month</th>
          <th>Payout Date</th>
          <th>Net Due</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {payslips.map(ps => (
          <tr
            key={ps.id}
            className="pf-payslip-row"
            onClick={() => navigate(`/worker/${worker.id}/payslip/${ps.id}`)}
          >
            <td>{cycleToLabel(ps.cycle_label)}</td>
            <td>{ps.payout_date ? formatDate(ps.payout_date) : '—'}</td>
            <td style={{ fontWeight: 600 }}>{formatCurrency(ps.net_due)}</td>
            <td>
              <span className={PAYSLIP_PILL[ps.status] || 'pill pill-pending'}>
                {PAYSLIP_LABEL[ps.status] || ps.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Compact deduction/earning hint for a leave type — mirrors DayDrawer.js leaveHint. */
function leaveHint(lt) {
  if (!lt) return '';
  if (lt.paid_leave) return 'Full day wages';
  const f = parseFloat(lt.penalty_factor ?? 0);
  if (f === 0)   return 'LOP only';
  if (f === 0.5) return 'LOP + ½ day deduction';
  if (f === 1)   return 'LOP + full day deduction';
  return `LOP + ${f}× daily rate`;
}

// ── LeaveRequestsTab ──────────────────────────────────────────────────────────

const LEAVE_STATUS_PILL = {
  draft:     'pill pill-pending',
  submitted: 'pill pill-pending',
  approved:  'pill pill-approved',
  rejected:  'pill pill-absent',
  cancelled: 'pill',
  archived:  'pill',
};

const LEAVE_STATUS_LABEL = {
  draft:     'Draft',
  submitted: 'Submitted',
  approved:  'Approved',
  rejected:  'Rejected',
  cancelled: 'Cancelled',
  archived:  'Archived',
};

/**
 * Leave Requests tab — apply for leave, review submitted requests, and approve/reject.
 * Supervisors can create and submit; managers can approve or reject.
 */
function LeaveRequestsTab({ worker, requests, onRefresh, isManager }) {
  const { currentUser } = useAuth();

  // Form state
  const [leaveTypes, setLeaveTypes]       = useState([]);      // approvable leave types from policy
  const [formLeaveType, setFormLeaveType] = useState('');
  const [formDuration, setFormDuration]   = useState('single');
  const [formDate, setFormDate]           = useState(getISTToday());
  const [formStartDate, setFormStartDate] = useState(getISTToday());
  const [formEndDate, setFormEndDate]     = useState(getISTToday());
  const [formReason, setFormReason]       = useState('');
  const [formSaving, setFormSaving]       = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);

  // Editing state — allows editing a draft request
  const [editingId, setEditingId] = useState(null);

  // Load approvable leave types from worker's policy
  useEffect(() => {
    const policyId = worker.leave_policy?.id ?? worker.leave_policy;
    if (!policyId) return;
    setPolicyLoading(true);
    getLeavePolicy(policyId)
      .then(r => {
        const types = (r.data?.leave_types ?? [])
          .map(lt => lt.leave_types_id)
          .filter(lt => lt && lt.approved_leave && lt.status === 'published');
        setLeaveTypes(types);
        // Auto-select when only one option exists
        if (types.length === 1) setFormLeaveType(String(types[0].id));
      })
      .catch(() => {})
      .finally(() => setPolicyLoading(false));
  }, [worker.leave_policy]);

  function resetForm() {
    if (leaveTypes.length === 1) {
      setFormLeaveType(String(leaveTypes[0].id));
    } else {
      setFormLeaveType('');
    }
    setFormDuration('single');
    setFormDate(getISTToday());
    setFormStartDate(getISTToday());
    setFormEndDate(getISTToday());
    setFormReason('');
    setEditingId(null);
  }

  function startEditing(req) {
    setEditingId(req.id);
    const ltId = typeof req.leave_type === 'object' ? req.leave_type?.id : req.leave_type;
    setFormLeaveType(String(ltId ?? ''));
    setFormDuration(req.leave_duration ?? 'single');
    setFormDate(req.date ?? getISTToday());
    setFormStartDate(req.start_date ?? getISTToday());
    setFormEndDate(req.end_date ?? getISTToday());
    setFormReason(req.reason ?? '');
  }

  async function handleSave(status) {
    if (!formLeaveType) return showAlert('Please select a leave type', 'error');
    if (formDuration === 'single' && !formDate) return showAlert('Please enter the leave date', 'error');
    if (formDuration === 'multiple' && (!formStartDate || !formEndDate)) return showAlert('Please enter start and end dates', 'error');

    setFormSaving(true);
    try {
      if (editingId) {
        // Saving an edited draft
        await updateLeaveRequest(editingId, {
          leave_type:     parseInt(formLeaveType),
          leave_duration: formDuration,
          ...(formDuration === 'single'
            ? { date: formDate }
            : { start_date: formStartDate, end_date: formEndDate }),
          reason: formReason || null,
          status,
        });
      } else {
        await createLeaveRequest({
          worker_id:    worker.id,
          leave_type_id: parseInt(formLeaveType),
          leave_duration: formDuration,
          date:       formDate,
          start_date: formStartDate,
          end_date:   formEndDate,
          reason:     formReason || undefined,
          status,
        });
      }
      showAlert(status === 'submitted' ? 'Leave request submitted' : 'Draft saved', 'success');
      resetForm();
      onRefresh();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to save leave request', 'error');
    } finally {
      setFormSaving(false);
    }
  }

  async function handleStatusChange(req, newStatus) {
    const fields = { status: newStatus };
    if ((newStatus === 'approved' || newStatus === 'rejected') && currentUser) {
      fields.user_approved  = currentUser.id;
      fields.date_approved  = new Date().toISOString();
    }
    try {
      await updateLeaveRequest(req.id, fields);
      showAlert(
        newStatus === 'approved' ? 'Leave request approved'
        : newStatus === 'rejected' ? 'Leave request rejected'
        : 'Leave request cancelled',
        newStatus === 'rejected' ? 'error' : 'success'
      );
      onRefresh();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to update leave request', 'error');
    }
  }

  // Format date range for display
  function formatLeaveDate(req) {
    if (req.leave_duration === 'multiple') {
      return `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`;
    }
    return req.date ? formatDate(req.date) : '—';
  }

  const hasForm = leaveTypes.length > 0;
  const today = getISTToday();

  return (
    <div className="pf-leave-tab fade-up">

      {/* ── Apply Leave form ── */}
      <div className="pf-leave-form-wrap">
        <div className="pf-section-head" style={{ marginBottom: 14 }}>
          <h2>{editingId ? 'Edit Leave Request' : 'Apply for Leave'}</h2>
        </div>

        {policyLoading ? (
          <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Loading leave types…</p>
        ) : leaveTypes.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
            No approvable leave types configured for this worker's policy.
          </p>
        ) : (
          <div className="pf-leave-form">
            {/* Leave type */}
            <div className="pf-leave-form-row">
              <label>Leave type</label>
              {leaveTypes.length === 1 ? (
                <span className="pf-leave-type-readonly">
                {leaveTypes[0].name}
                <small className="pf-leave-type-hint">{leaveHint(leaveTypes[0])}</small>
              </span>
              ) : (
                <select
                  value={formLeaveType}
                  onChange={e => setFormLeaveType(e.target.value)}
                >
                  <option value="">Select leave type…</option>
                  {leaveTypes.map(lt => (
                    <option key={lt.id} value={lt.id}>{lt.name} — {leaveHint(lt)}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Duration */}
            <div className="pf-leave-form-row">
              <label>Duration</label>
              <div className="pf-leave-duration-toggle">
                <button
                  className={formDuration === 'single' ? 'active' : ''}
                  onClick={() => setFormDuration('single')}
                >Single Day</button>
                <button
                  className={formDuration === 'multiple' ? 'active' : ''}
                  onClick={() => setFormDuration('multiple')}
                >Multiple Days</button>
              </div>
            </div>

            {/* Date(s) */}
            {formDuration === 'single' ? (
              <div className="pf-leave-form-row">
                <label>Date</label>
                <input
                  type="date"
                  value={formDate}
                  min={today}
                  onChange={e => setFormDate(e.target.value)}
                />
              </div>
            ) : (
              <>
                <div className="pf-leave-form-row">
                  <label>From</label>
                  <input
                    type="date"
                    value={formStartDate}
                    min={today}
                    onChange={e => setFormStartDate(e.target.value)}
                  />
                </div>
                <div className="pf-leave-form-row">
                  <label>To</label>
                  <input
                    type="date"
                    value={formEndDate}
                    min={formStartDate || today}
                    onChange={e => setFormEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Reason */}
            <div className="pf-leave-form-row">
              <label>Reason <span style={{ color: 'var(--fg-muted)' }}>(optional)</span></label>
              <textarea
                rows={2}
                placeholder="Enter reason for leave…"
                value={formReason}
                onChange={e => setFormReason(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="pf-leave-form-actions">
              {editingId && (
                <button className="lv-form-btn lv-form-btn-quiet" onClick={resetForm} disabled={formSaving}>
                  Cancel edit
                </button>
              )}
              <button
                className="lv-form-btn lv-form-btn-outline"
                onClick={() => handleSave('draft')}
                disabled={formSaving}
              >
                Save draft
              </button>
              <button
                className="lv-form-btn lv-form-btn-primary"
                onClick={() => handleSave('submitted')}
                disabled={formSaving || !formLeaveType}
              >
                Submit for approval
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Existing requests list ── */}
      {requests.length > 0 && (
        <div className="pf-leave-list">
          <div className="pf-section-head" style={{ marginTop: 24, marginBottom: 12 }}>
            <h2>Leave Requests</h2>
          </div>
          <div className="pf-leave-items">
            {requests.map(req => {
              const ltName = typeof req.leave_type === 'object'
                ? req.leave_type?.name
                : leaveTypes.find(lt => String(lt.id) === String(req.leave_type))?.name ?? '—';
              const isDraft     = req.status === 'draft';
              const isSubmitted = req.status === 'submitted';
              const isFinal     = ['approved', 'rejected', 'cancelled', 'archived'].includes(req.status);

              return (
                <div key={req.id} className="pf-leave-item">
                  <div className="pf-leave-item-main">
                    <div className="pf-leave-item-date">{formatLeaveDate(req)}</div>
                    <div className="pf-leave-item-type">{ltName}</div>
                    {req.reason && <div className="pf-leave-item-reason">{req.reason}</div>}
                    {req.user_approved && req.date_approved && (
                      <div className="pf-leave-item-approved-by">
                        {req.status === 'rejected' ? 'Rejected' : 'Approved'} by{' '}
                        {req.user_approved.first_name} {req.user_approved.last_name}
                        {' · '}{formatDate(req.date_approved)}
                      </div>
                    )}
                  </div>
                  <div className="pf-leave-item-meta">
                    <span className={LEAVE_STATUS_PILL[req.status] || 'pill'}>
                      {LEAVE_STATUS_LABEL[req.status] || req.status}
                    </span>
                    <div className="pf-leave-item-actions">
                      {/* Supervisor actions — draft only */}
                      {isDraft && (
                        <>
                          <button className="lv-btn lv-btn-quiet" onClick={() => startEditing(req)}>Edit</button>
                          <button className="lv-btn lv-btn-primary" onClick={() => handleStatusChange(req, 'submitted')}>Submit</button>
                          <button className="lv-btn lv-btn-quiet" onClick={() => handleStatusChange(req, 'cancelled')}>Cancel</button>
                        </>
                      )}
                      {/* Supervisor cancel on submitted — no edit allowed once submitted */}
                      {isSubmitted && !isManager && (
                        <button className="lv-btn lv-btn-quiet" onClick={() => handleStatusChange(req, 'cancelled')}>Cancel</button>
                      )}
                      {/* Manager actions on submitted */}
                      {isSubmitted && isManager && (
                        <>
                          <button className="lv-btn lv-btn-approve" onClick={() => handleStatusChange(req, 'approved')}>Approve</button>
                          <button className="lv-btn lv-btn-reject"  onClick={() => handleStatusChange(req, 'rejected')}>Reject</button>
                          <button className="lv-btn lv-btn-quiet"   onClick={() => handleStatusChange(req, 'cancelled')}>Cancel</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ViewWorkerPage ────────────────────────────────────────────────────────────

export default function ViewWorkerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isManager } = useAuth();

  const [worker,          setWorker]          = useState(null);
  const [attendance,      setAttendance]      = useState([]);
  const [earnings,        setEarnings]        = useState([]);
  const [notes,           setNotes]           = useState([]);
  const [payouts,         setPayouts]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  // Payslips load independently so they never delay the main page render
  const [payslips,        setPayslips]        = useState([]);
  const [payslipsLoading, setPayslipsLoading] = useState(true);
  // Leave requests load independently — non-blocking
  const [leaveRequests,        setLeaveRequests]        = useState([]);
  const [leaveRequestsLoading, setLeaveRequestsLoading] = useState(true);
  // Deductions load independently — non-blocking
  const [deductions,           setDeductions]           = useState([]);

  const [tab,         setTab]         = useState('attendance');
  const [attView,     setAttView]     = useState('calendar'); // 'calendar' | 'list'
  const [drawer,      setDrawer]      = useState(null);       // attendance record or null
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Five core data sources — controls the page loading spinner
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      getWorker(id).then(r => setWorker(r.data)),
      getWorkerAttendance(id).then(r => setAttendance(r.data || [])),
      getWorkerEarnings(id).then(r => setEarnings(r.data || [])),
      getWorkerNotes(id).then(r => setNotes(r.data || [])),
      getWorkerPayouts(id).then(r => setPayouts(r.data || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Payslips fetch runs in parallel but does NOT block the page render —
  // a slow payslips query will never delay the attendance/notes/transactions view.
  useEffect(() => {
    if (!id) return;
    setPayslipsLoading(true);
    getWorkerPayslips(id)
      .then(r => setPayslips(r.data || []))
      .catch(() => setPayslips([]))
      .finally(() => setPayslipsLoading(false));
  }, [id]);

  // Leave requests fetch — also non-blocking
  useEffect(() => {
    if (!id) return;
    setLeaveRequestsLoading(true);
    getWorkerLeaveRequests(id)
      .then(r => setLeaveRequests(r.data || []))
      .catch(() => setLeaveRequests([]))
      .finally(() => setLeaveRequestsLoading(false));
  }, [id]);

  const refreshLeaveRequests = useCallback(() => {
    getWorkerLeaveRequests(id).then(r => setLeaveRequests(r.data || [])).catch(() => {});
  }, [id]);

  // Deductions fetch — non-blocking parallel load
  useEffect(() => {
    if (!id) return;
    getWorkerDeductions(id).then(r => setDeductions(r.data || [])).catch(() => {});
  }, [id]);


  const refreshAttendance = useCallback(() => {
    getWorkerAttendance(id).then(r => setAttendance(r.data || []));
  }, [id]);

  const refreshPayouts = useCallback(() => {
    getWorkerPayouts(id).then(r => setPayouts(r.data || []));
  }, [id]);

  const refreshPayslips = useCallback(() => {
    getWorkerPayslips(id).then(r => setPayslips(r.data || []));
  }, [id]);

  // Compute current-month attendance stats
  const attStats = useMemo(() => {
    const now = new Date();
    const thisMonth = attendance.filter(r => {
      const [y, mo] = r.date.split('-').map(Number);
      return y === now.getFullYear() && mo === now.getMonth() + 1;
    });
    const present = thisMonth.filter(r => attCategory(r.status) === 'present').length;
    const absent  = thisMonth.filter(r => r.status === 'absent').length;
    const leave   = thisMonth.filter(r => r.status === 'leave').length;
    const off     = thisMonth.filter(r => r.status === 'weekly_off').length;
    // Attendance rate counts only shift days (present vs. absent); leave/off are excluded
    const total   = present + absent;
    const rate    = total > 0 ? Math.round((present / total) * 100) : 100;
    return { present, absent, leave, off, rate };
  }, [attendance]);

  if (loading) return <div className="loader">Loading…</div>;
  if (!worker)  return <div className="loader">Worker not found.</div>;

  const tabs = [
    { key: 'attendance',   label: 'Attendance',   count: attendance.length },
    { key: 'notes',        label: 'Notes',        count: notes.length },
    { key: 'transactions', label: 'Transactions', count: earnings.length + payouts.length + deductions.length },
    { key: 'leaves',       label: 'Leaves',       count: leaveRequests.length || undefined },
    { key: 'payslips',     label: 'Payslips' },
  ];

  return (
    <div className="app-shell">
      <div className="pf-page">

        {/* ── Header ── */}
        <header className="pf-header">
          <div className="pf-back-row">
            <button className="icon-btn" onClick={() => navigate(location.state?.from ?? '/')} aria-label="Back">
              <span className="material-symbols-rounded">arrow_back</span>
            </button>
            <h1>Worker Profile</h1>
          </div>
          <div className="pf-header-actions">
            <button className="icon-btn" aria-label="More options">
              <span className="material-symbols-rounded">more_vert</span>
            </button>
          </div>
        </header>

        {/* ── Left column (identity + balance + actions) ── */}
        <div className="pf-left-col">
          <IdentityCard worker={worker} />
          <BalanceCard worker={worker} />
          <QuickActionBar
            worker={worker}
            isManager={isManager}
            onNote={() => setTab('notes')}
            onPay={() => setPaymentOpen(true)}
          />
        </div>

        {/* ── Right column (tabs + content) ── */}
        <div className="pf-right-col">
          <ProfileTabBar tabs={tabs} current={tab} onChange={setTab} />

          {/* Attendance */}
          {tab === 'attendance' && (
            <div className="fade-up">
              <div className="pf-section-head">
                <h2>This Month</h2>
                <div className="pf-subtoggle" role="tablist">
                  <button aria-pressed={attView === 'calendar'} onClick={() => setAttView('calendar')}>
                    <span className="material-symbols-rounded">calendar_month</span> Calendar
                  </button>
                  <button aria-pressed={attView === 'list'} onClick={() => setAttView('list')}>
                    <span className="material-symbols-rounded">view_list</span> List
                  </button>
                </div>
              </div>

              <div className="pf-stats-row">
                <div className="pf-stat">
                  <span className="pf-stat-num num" style={{ color: '#4d6325' }}>{attStats.present}</span>
                  <span className="pf-stat-label">Present</span>
                </div>
                <div className="pf-stat">
                  <span className="pf-stat-num num" style={{ color: '#8a2f1a' }}>{attStats.absent}</span>
                  <span className="pf-stat-label">Absent</span>
                </div>
                {attStats.leave > 0 && (
                  <div className="pf-stat">
                    <span className="pf-stat-num num" style={{ color: '#3a6e8e' }}>{attStats.leave}</span>
                    <span className="pf-stat-label">Leave</span>
                  </div>
                )}
                {attStats.off > 0 && (
                  <div className="pf-stat">
                    <span className="pf-stat-num num" style={{ color: '#6b5fa5' }}>{attStats.off}</span>
                    <span className="pf-stat-label">Off</span>
                  </div>
                )}
                <div className="pf-stat">
                  <span className="pf-stat-num num">{attStats.rate}%</span>
                  <span className="pf-stat-label">Attendance</span>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                {attView === 'calendar'
                  ? <CalendarView records={attendance} onPick={setDrawer} />
                  : <AttendanceList records={attendance} onPick={setDrawer} />
                }
              </div>
            </div>
          )}

          {/* Notes */}
          {tab === 'notes' && (
            <div className="fade-up" style={{ marginTop: 18 }}>
              <NotesTab notes={notes} onOpenDrawer={setDrawer} />
            </div>
          )}

          {/* Transactions */}
          {tab === 'transactions' && (
            <div className="fade-up" style={{ marginTop: 18 }}>
              <TransactionsTab earnings={earnings} payouts={payouts} deductions={deductions} worker={worker} attendance={attendance} onOpenDrawer={setDrawer} />
            </div>
          )}

          {/* Leaves */}
          {tab === 'leaves' && (
            <div className="fade-up" style={{ marginTop: 18 }}>
              {leaveRequestsLoading ? (
                <p style={{ color: 'var(--fg-muted)', fontSize: 13, padding: '8px 2px' }}>Loading…</p>
              ) : (
                <LeaveRequestsTab
                  worker={worker}
                  requests={leaveRequests}
                  onRefresh={refreshLeaveRequests}
                  isManager={isManager}
                />
              )}
            </div>
          )}

          {/* Payslips */}
          {tab === 'payslips' && (
            <div className="fade-up" style={{ marginTop: 18 }}>
              <PayslipsTab worker={worker} payslips={payslips} payslipsLoading={payslipsLoading} />
            </div>
          )}
        </div>

        {/* Mobile Pay FAB */}
        {isManager && (
          <button className="pf-pay-fab" onClick={() => setPaymentOpen(true)}>
            <span className="material-symbols-rounded">currency_rupee</span>
            Pay {worker.name?.split(' ')[0]}
          </button>
        )}
      </div>

      {/* Day drawer — bottom-sheet attendance detail */}
      {drawer && (
        <DayDrawer
          record={drawer}
          onClose={() => setDrawer(null)}
          onApproved={refreshAttendance}
        />
      )}

      {/* Payment modal — advance / salary payout */}
      <PaymentModal
        open={paymentOpen}
        workerId={worker.id}
        workerName={worker.name}
        onClose={() => setPaymentOpen(false)}
        onSaved={refreshPayouts}
      />
    </div>
  );
}
